"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function AddCategoryPage ({ isEdit = false, editingItem = null, onSectionChange }) {
    const [formData, setFormData] = useState({
        title: "",
        image: "",
    });
    const [imagePreview, setImagePreview] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    // Cleanup object URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];

        if (file) {
            // Set the file in formData
            setFormData(prev => ({
                ...prev,
                image: file
            }));

            // Create preview URL for the selected image
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            // Clear preview if no file selected
            setFormData(prev => ({
                ...prev,
                image: ""
            }));
            setImagePreview("");
        }
    };

    const handleRemoveImage = () => {
        // Revoke the current object URL
        if (imagePreview && imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
        }

        setFormData(prev => ({
            ...prev,
            image: ""
        }));
        setImagePreview("");

        // Clear the file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const data = new FormData();
            data.append("title", formData.title);
            data.append("image", formData.image);

            const endpoint = "https://leapatinternational.com/admin/api/v1/category_add.php";

            const response = await fetch(endpoint, {
                method: "POST",
                body: data,
            });
            const result = await response.json();

            if (result.status === "success") {
                toast.success("Category created successfully!");
                navigate('/dashboard/categories');
            } else {
                toast.error(result.message || ("Failed to create category"));
            }
        } catch (err) {
            console.error("Error:", err);
            toast.error("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/dashboard/categories');
    };

    return (
        <div className="category-management-section">
            {/* Page Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between mb-3">

                        <div>
                            <h1 className="page-title mb-0">
                                {isEdit ? "Edit Category" : "Add New Category"}
                            </h1>
                            <p className="page-subtitle mb-0">
                                {isEdit ? "Update category information" : "Create a new product category"}
                            </p>
                        </div>
                        <button
                            className="back-button me-3"
                            onClick={handleCancel}
                        >
                            ← Back to Categories
                        </button>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Category Information</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Category Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                placeholder="Enter category name"
                                                required
                                            />
                                            <div className="form-text">Choose a clear, descriptive name for your category</div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Category Image</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                            />
                                            <div className="form-text">Upload an image to represent this category (JPG, PNG, GIF)</div>

                                            {imagePreview && (
                                                <div className="mt-3">
                                                    <label className="form-label">Image Preview</label>
                                                    <div className="image-preview-container position-relative">
                                                        <img
                                                            src={imagePreview}
                                                            alt="Category preview"
                                                            className="img-thumbnail"
                                                            style={{ width: "200px", height: "200px", objectFit: "cover" }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                                                            onClick={handleRemoveImage}
                                                            style={{ transform: "translate(25%, -25%)" }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex gap-2 pt-3 border-top">
                                    <button
                                        type="submit"
                                        className="add-button"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                {isEdit ? "Updating..." : "Creating..."}
                                            </>
                                        ) : (
                                            <>
                                                {isEdit ? "Update Category" : "Create Category"}
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="back-button ps-3 pe-3"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}